import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useStore } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { useAppDispatch, useAppSelector } from 'src/store';
import { SecondaryButton, PrimaryButton } from 'src/shared/components/button/Button';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import { Access, Role } from 'src/shared/types';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import StatusIndicator from 'src/shared/components/status-indicator/StatusIndicator';
import useAuthService from 'src/shared/hooks/useAuthService';
import { isCountryCodeValid as checkIsCountryCodeValid } from 'src/shared/helpers/phoneNumberHelpers';
import AssignAdminRoles from 'src/app/admin/views/user-management/components/assign-admin-roles/AssignAdminRoles';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import usePermissions from 'src/shared/hooks/usePermissions';
import { formatPhoneNumberWithCountryCode } from 'src/app/admin/views/user-management/components/userManagementHelpers';
import { getUserStatusFromEnum } from 'src/app/admin/views/user-management/userManagementHelpers';
import useBoolean from 'src/shared/hooks/useBoolean';
import { LoaderInline } from 'src/shared/components/loader/Loader';
import { UserX as PersonOffIcon, User as PersonIcon, Save as SaveIcon } from 'lucide-react';
import type { RootState } from 'src/store';
import ReassignUserToOrganisation from './components/ReassignUserToOrganisation';
import UnassignUserFromOrganisation from './components/UnassignUserFromOrganisation';
import EditProjectUserHeader from './components/EditProjectUserHeader';
import EditProjectUserActivatePanel from './components/EditProjectUserActivatePanel';
import EditProjectUserProfileFieldsStep from './components/EditProjectUserProfileFieldsStep';
import styles from './EditProjectUser.module.scss';
import { EDIT_USER_SUCCESS_MESSAGE } from './editProjectUserConstants';
import type { EditUserUnsavedWorkState } from './editProjectUserTypes';
import { useEditProjectUserUnsavedChanges } from './useEditProjectUserUnsavedChanges';
import {
    useUpdateProjectUserMutation,
    useEnableUserMutation,
    useDisableUserMutation,
    useAdminUpdateUserMutation,
    useUpsertUserProjectsForCustomerMutation,
    useRemoveUserProjectsForCustomerMutation,
} from '../../state/api/userManagementGraphSlice';
import {
    normalizeUserProjectsForEquality,
    planUserProjectAccessMutations,
    type PersistUserProjectAccessPayload,
} from '../../userProjectAccessMutations';
import AssignProjects from '../assign-projects/AssignProjects';
import UserDetails from '../user-details/UserDetails';
import UserManagementDrawerLayout from '../user-drawer-layout/UserManagementDrawerLayout';
import {
    EditProjectUserFormState,
    setEditUserFormState,
    setEditUserView,
    setEditUserFormMode,
    updateGeneralUiState,
    reset,
} from '../../state/userManagementSlice';
import { getEditProjectUserFormState, getEditProjectUserFormStep } from '../../state/userManagementSelector';
import { patchManagementListsAfterProjectSync, patchManagementListsAfterUserEditSave } from '../../patchUserManagementListCaches';

export type { EditUserUnsavedWorkState } from './editProjectUserTypes';

type EditProjectUserProps = {
    /** Lets the modal shell show close guards while edits or async saves are in progress */
    onUnsavedWorkStateChange?: (state: EditUserUnsavedWorkState) => void;
};

/**
 * Edit-user drawer body: main edit-user experience inside `UserManagementModals`.
 * Step is driven by Redux `editUserView` (see `EditProjectUserFormState` in userManagementSlice).
 * Handles profile + org roles + site permissions, activate/deactivate, and assign/unassign flows.
 */
export const EditProjectUser = ({ onUnsavedWorkStateChange }: EditProjectUserProps) => {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const { currentCustomerProjectIds } = useCustomer();
    const { isCurrentUserSuperAdmin, isCurrentUserOnboardingManager } = useAuthService();
    const { currentCustomerId: customerId } = useCustomer();
    const [errorMessage, setErrorMessage] = useState('');
    const { hasCustomerLevelRole } = usePermissions();
    const [isCountryCodeValid, setIsCountryCodeValid] = useState<boolean>(true);
    const [isSubmitting, submittingHelpers] = useBoolean(false);
    const formState = useAppSelector(getEditProjectUserFormState);
    const currentEditStep = useAppSelector(getEditProjectUserFormStep);
    /** Count of concurrent matrix/API saves so the footer can show “Saving…” instead of Save */
    const [inFlightSitePermissionSaves, setInFlightSitePermissionSaves] = useState(0);
    const [updateProjectUser] = useUpdateProjectUserMutation();
    const [enableUserMutation] = useEnableUserMutation();
    const [disableUserMutation] = useDisableUserMutation();
    const [adminUpdateUser] = useAdminUpdateUserMutation();
    const [upsertUserProjectsForCustomer] = useUpsertUserProjectsForCustomerMutation();
    const [removeUserProjectsForCustomer] = useRemoveUserProjectsForCustomerMutation();
    /** Inactive users cannot edit site permissions; invited/active can */
    const isActiveOrInvited = formState.status === 'Active' || formState.status === 'Invited';

    const isAssignedToCustomer = formState.customers.some(customer => customer.customerId === customerId && customer.active);
    const editedUserDetailsCard = (
        <div className={styles.header}>
            <UserDetails {...formState} />
        </div>
    );

    const {
        savedOrganisationRoles,
        isProfilePhoneValid,
        profileSaveDisabled,
        organisationRolesChanged,
        profileFieldsChangedSinceLastSave,
        hasUnsavedMainViewEdits,
        showSaveOnDefaultView,
        lastPersistedSiteAssignmentsRef,
        profileBaselineRef,
        setPersistedAssignmentsVersion,
        setProfileBaselineVersion,
    } = useEditProjectUserUnsavedChanges({
        formState,
        customerId,
        currentCustomerProjectIds,
        isActiveOrInvited,
        inFlightSitePermissionSaves,
        isSubmitting,
        onUnsavedWorkStateChange,
    });

    const userStatusIndicator = <StatusIndicator status={getUserStatusFromEnum(formState.status)} statusText={formState.status} />;

    const updateEditUserForm = (partialForm: Partial<EditProjectUserFormState['formState']>) => {
        dispatch(setEditUserFormState({ ...partialForm }));
    };

    const notifyUserByEmailCheckbox = (
        <Checkbox
            label={'Send user an email to notify them of this change'}
            checked={formState.notify}
            onChange={value => updateEditUserForm({ notify: value })}
            className={styles.notifyUser}
        />
    );

    const activeStatusText = isActiveOrInvited ? 'Deactivate' : 'Reactivate';
    const activeStatusIcon = isActiveOrInvited ? <PersonOffIcon /> : <PersonIcon />;
    const assignStatusText = isAssignedToCustomer ? 'Unassign' : 'Assign';

    /** Closes the edit modal and resets form slice; optionally shows the global success snackbar */
    const dismissEditUserWithSuccess = (message: string, shouldShowSuccessAlert: boolean = true) => {
        dispatch(reset());
        dispatch(
            updateGeneralUiState({
                shouldShowEditUserModal: false,
                shouldShowSuccessAlert: shouldShowSuccessAlert,
                successStatusMessage: message,
            })
        );
    };

    /** Enable/disable account from the activate-or-deactivate step */
    const submitUserActivationChange = async () => {
        if (isSubmitting) {
            return;
        }

        submittingHelpers.on();
        try {
            if (isActiveOrInvited) {
                await disableUserMutation({
                    email: formState.email,
                    notify: formState.notify,
                }).unwrap();
                dismissEditUserWithSuccess(EDIT_USER_SUCCESS_MESSAGE);
            } else {
                await enableUserMutation({
                    email: formState.email,
                    notify: formState.notify,
                }).unwrap();

                dispatch(
                    updateGeneralUiState({
                        shouldShowSuccessAlert: true,
                        successStatusMessage: EDIT_USER_SUCCESS_MESSAGE,
                    })
                );
                dispatch(setEditUserView('default'));
            }
        } catch (error) {
            console.error('rejected', error);
            setErrorMessage('Something went wrong. Please try again later.');
        } finally {
            submittingHelpers.off();
        }
    };

    /** Persists name/phone/notify, project list for this customer, and (for super/onboarding) admin roles via admin API */
    const saveUserProfileAndRoles = async () => {
        if (isSubmitting) {
            return;
        }

        submittingHelpers.on();
        try {
            const phoneNumber = formatPhoneNumberWithCountryCode(formState.phoneCountryCode, formState.phone);
            await updateProjectUser({
                ...formState,
                tnc_consent: formState.tnc_consent ? true : false,
                customerId: customerId,
                phone: phoneNumber,
                notify: formState.notify,
            }).unwrap();

            if (isCurrentUserSuperAdmin || isCurrentUserOnboardingManager) {
                const { firstName, lastName, cognitoId, projects } = formState;
                const updatedUserParams = {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    cognitoId,
                    projects,
                    tnc_consent: formState.tnc_consent ? true : false,
                };
                await adminUpdateUser({
                    ...updatedUserParams,
                    phone: phoneNumber,
                    customers: [
                        {
                            customerId: customerId,
                            roles: [...(formState.roles || [])],
                        },
                    ],
                    tnc_consent: formState.tnc_consent ? true : false,
                }).unwrap();
            }

            patchManagementListsAfterUserEditSave(dispatch, store.getState(), {
                cognitoId: formState.cognitoId,
                customerId,
                firstName: formState.firstName ?? '',
                lastName: formState.lastName ?? '',
                phone: formState.phone ?? '',
                phoneCountryCode: formState.phoneCountryCode ?? '',
                tnc_consent: Boolean(formState.tnc_consent),
                roles: formState.roles || [],
                projects: formState.projects,
            });
            if (profileBaselineRef.current && formState.cognitoId) {
                profileBaselineRef.current = {
                    firstName: formState.firstName ?? '',
                    lastName: formState.lastName ?? '',
                    phone: formState.phone ?? '',
                    phoneCountryCode: formState.phoneCountryCode ?? '',
                    notify: formState.notify,
                };
                setProfileBaselineVersion(e => e + 1);
            }
            lastPersistedSiteAssignmentsRef.current = formState.projects.map(p => ({
                projectId: p.projectId,
                roles: p.roles ?? null,
            }));
            setPersistedAssignmentsVersion(v => v + 1);
            setErrorMessage('');
            dispatch(
                updateGeneralUiState({
                    shouldShowSuccessAlert: true,
                    successStatusMessage: EDIT_USER_SUCCESS_MESSAGE,
                })
            );
        } catch (error) {
            console.error('rejected', error);
            setErrorMessage('Something went wrong. Please try again later.');
        } finally {
            submittingHelpers.off();
        }
    };

    /**
     * Default-step save: syncs site permission deltas first, then profile/roles if still dirty
     * (matrix may auto-save; this reconciles remaining changes).
     */
    const saveMainViewEdits = async () => {
        if (isSubmitting) {
            return;
        }
        submittingHelpers.on();
        setErrorMessage('');
        try {
            const syncedProjects = formState.projects.filter(p => currentCustomerProjectIds.includes(p.projectId));
            const accessMutationPlan = planUserProjectAccessMutations(
                lastPersistedSiteAssignmentsRef.current ?? [],
                syncedProjects,
                currentCustomerProjectIds
            );
            const sitePermissionsChanged = accessMutationPlan.removeProjectIds.length > 0 || accessMutationPlan.upserts.length > 0;
            if (sitePermissionsChanged) {
                await persistUserProjectAccess({ syncedProjects });
            }
            const refList = lastPersistedSiteAssignmentsRef.current ?? [];
            const siteAssignmentsStillChanged = !isEqual(
                normalizeUserProjectsForEquality(formState.projects, currentCustomerProjectIds),
                normalizeUserProjectsForEquality(refList, currentCustomerProjectIds)
            );
            if (organisationRolesChanged || profileFieldsChangedSinceLastSave || siteAssignmentsStillChanged) {
                await saveUserProfileAndRoles();
            } else if (sitePermissionsChanged) {
                dispatch(
                    updateGeneralUiState({
                        shouldShowSuccessAlert: true,
                        successStatusMessage: EDIT_USER_SUCCESS_MESSAGE,
                    })
                );
            }
        } catch (error) {
            console.error('rejected', error);
            setErrorMessage('Something went wrong. Please try again later.');
        } finally {
            submittingHelpers.off();
        }
    };

    const isMainViewSaveDisabled =
        isSubmitting ||
        formState.disableSubmit ||
        !hasUnsavedMainViewEdits ||
        (profileFieldsChangedSinceLastSave && profileSaveDisabled) ||
        (organisationRolesChanged && !isProfilePhoneValid);

    const mainViewSaveButton = showSaveOnDefaultView ? (
        <PrimaryButton onClick={saveMainViewEdits} disabled={isMainViewSaveDisabled} data-testid='user-management-edit-user-save-changes'>
            <SaveIcon />
            &nbsp;{isSubmitting ? 'Saving...' : 'Save changes'}
        </PrimaryButton>
    ) : null;

    const mainViewSaveFooter =
        inFlightSitePermissionSaves > 0 ? (
            <div className={styles.footerSavingChanges} role='status' aria-live='polite' data-testid='matrix-inline-saving'>
                <LoaderInline />
                <span>Saving changes...</span>
            </div>
        ) : (
            mainViewSaveButton
        );

    const profileEditorSaveButton = (
        <PrimaryButton
            onClick={saveUserProfileAndRoles}
            disabled={profileSaveDisabled || isSubmitting || formState.disableSubmit}
            data-testid='user-management-edit-user-save-profile'
        >
            <SaveIcon />
            &nbsp;{isSubmitting ? 'Saving...' : 'Save changes'}
        </PrimaryButton>
    );

    const backButton = (
        <SecondaryButton
            onClick={() => {
                setErrorMessage('');
                dispatch(setEditUserView('default'));
            }}
        >
            Back
        </SecondaryButton>
    );

    const isCurrentUserCustomerAdmin = hasCustomerLevelRole({
        customerId,
        role: Role.CUSTOMER_ADMIN,
    });
    const isAllowedToAssignOrUnassign = isCurrentUserCustomerAdmin;

    /** Applies remove/upsert project access for the current customer; updates caches and baseline refs on success */
    const persistUserProjectAccess = useCallback(
        async ({ syncedProjects }: PersistUserProjectAccessPayload) => {
            if (!formState.cognitoId || !formState.id) {
                return;
            }
            const plan = planUserProjectAccessMutations(
                lastPersistedSiteAssignmentsRef.current ?? [],
                syncedProjects,
                currentCustomerProjectIds
            );
            if (plan.removeProjectIds.length === 0 && plan.upserts.length === 0) {
                return;
            }
            setInFlightSitePermissionSaves(c => c + 1);
            try {
                if (plan.removeProjectIds.length > 0) {
                    await removeUserProjectsForCustomer({
                        customerId,
                        userId: formState.id,
                        projectIds: plan.removeProjectIds,
                    }).unwrap();
                }
                if (plan.upserts.length > 0) {
                    await upsertUserProjectsForCustomer({
                        customerId,
                        userId: formState.id,
                        projects: plan.upserts,
                    }).unwrap();
                }
                lastPersistedSiteAssignmentsRef.current = syncedProjects.map(p => ({
                    projectId: p.projectId,
                    roles: p.roles ?? null,
                }));
                setPersistedAssignmentsVersion(v => v + 1);
                patchManagementListsAfterProjectSync(dispatch, store.getState(), {
                    cognitoId: formState.cognitoId,
                    customerId,
                    projects: syncedProjects,
                });
            } catch {
                setErrorMessage('Could not update site permissions. Please try again.');
                throw new Error('user site permissions update failed');
            } finally {
                setInFlightSitePermissionSaves(c => Math.max(0, c - 1));
            }
        },
        [
            formState.cognitoId,
            formState.id,
            customerId,
            currentCustomerProjectIds,
            dispatch,
            removeUserProjectsForCustomer,
            upsertUserProjectsForCustomer,
            store,
        ]
    );

    /** Keeps matrix persist baseline and user lists aligned after a server-side bulk site-role apply. */
    const onSitePermissionsBulkSynced = useCallback(
        (syncedProjects: EditProjectUserFormState['formState']['projects']) => {
            lastPersistedSiteAssignmentsRef.current = syncedProjects.map(p => ({
                projectId: p.projectId,
                roles: p.roles ?? null,
            }));
            setPersistedAssignmentsVersion(v => v + 1);
            if (formState.cognitoId && customerId) {
                patchManagementListsAfterProjectSync(dispatch, store.getState(), {
                    cognitoId: formState.cognitoId,
                    customerId,
                    projects: syncedProjects,
                });
            }
        },
        [customerId, dispatch, formState.cognitoId, lastPersistedSiteAssignmentsRef, setPersistedAssignmentsVersion, store]
    );

    /** Validates dial code shape for profile step / save gating */
    const handlePhoneCountryBlur = () => {
        setIsCountryCodeValid(!formState?.phoneCountryCode || checkIsCountryCodeValid(formState?.phoneCountryCode));
    };

    const disableProjectPermissions = !isActiveOrInvited || (currentCustomerProjectIds?.length ?? 0) < 1;
    const isAllowedToActivateOrDeactivate = isCurrentUserSuperAdmin;

    const assignedOrgActions =
        isAssignedToCustomer && (isAllowedToActivateOrDeactivate || isAllowedToAssignOrUnassign) ? (
            <>
                {isAllowedToActivateOrDeactivate && (
                    <button
                        type='button'
                        className={classNames(styles.footerTextLink, isActiveOrInvited && styles.footerTextLinkDestructive)}
                        onClick={() => dispatch(setEditUserView('activate-or-deactivate'))}
                    >
                        {`${activeStatusText} account`}
                    </button>
                )}
                {isAllowedToActivateOrDeactivate && isAllowedToAssignOrUnassign && (
                    <span className={styles.footerLinkSep} aria-hidden>
                        ·
                    </span>
                )}
                {isAllowedToAssignOrUnassign && (
                    <button
                        type='button'
                        className={styles.footerTextLink}
                        onClick={() => {
                            dispatch(setEditUserFormMode(Access.UPDATE));
                            dispatch(setEditUserView(isAssignedToCustomer ? 'unassign' : 'reassign'));
                        }}
                    >
                        {`${assignStatusText} ${isAssignedToCustomer ? 'from' : 'to'} this organisation`}
                    </button>
                )}
            </>
        ) : null;

    return (
        <div className={styles.editProjectUser} data-testid='user-management-edit-user-panel'>
            <div className={styles.title}>Edit user</div>

            {currentEditStep === 'default' && (
                <div className={styles.sectionContainer}>
                    {isAssignedToCustomer ? (
                        <UserManagementDrawerLayout
                            variant='stacked'
                            sidebar={
                                <>
                                    <div className={styles.section}>
                                        <EditProjectUserHeader setErrorMessage={setErrorMessage} assignedOrgActions={assignedOrgActions} />
                                    </div>
                                    {(isCurrentUserSuperAdmin || isCurrentUserOnboardingManager) && (
                                        <div className={styles.section}>
                                            <div className={styles.sectionHeading}>Admin roles</div>
                                            <AssignAdminRoles
                                                onChange={updateEditUserForm}
                                                roles={formState.roles}
                                                defaultRoles={savedOrganisationRoles as Role[]}
                                            />
                                        </div>
                                    )}
                                </>
                            }
                            main={
                                <div className={styles.section}>
                                    <div className={styles.sectionHeading}>Site permissions</div>
                                    <AssignProjects
                                        formState={formState}
                                        onFormChange={updateEditUserForm}
                                        formMode={isActiveOrInvited ? Access.UPDATE : Access.VIEW}
                                        disabled={disableProjectPermissions}
                                        isSavingChanges={isSubmitting || inFlightSitePermissionSaves > 0}
                                        persistUserProjectAccess={
                                            formState.cognitoId && Boolean(formState.id) && isActiveOrInvited && !disableProjectPermissions
                                                ? persistUserProjectAccess
                                                : undefined
                                        }
                                        onSitePermissionsBulkSynced={onSitePermissionsBulkSynced}
                                    />
                                </div>
                            }
                        />
                    ) : (
                        <div className={styles.section}>
                            <EditProjectUserHeader setErrorMessage={setErrorMessage} />
                        </div>
                    )}

                    {isAssignedToCustomer && (
                        <>
                            <span className={styles.sectionDivider}></span>
                            {isActiveOrInvited && <div>{notifyUserByEmailCheckbox}</div>}
                            <div className={styles.bottomNavigation} data-testid='edit-project-user-default-footer'>
                                {mainViewSaveFooter}
                            </div>
                        </>
                    )}
                    {!isAssignedToCustomer && isAllowedToAssignOrUnassign && (
                        <>
                            <span className={styles.sectionDivider}></span>
                            <div className={styles.bottomNavigation}>
                                <div className={styles.footerInlineActions}>
                                    <button
                                        type='button'
                                        className={styles.footerTextLink}
                                        onClick={() => {
                                            dispatch(setEditUserFormMode(Access.UPDATE));
                                            dispatch(setEditUserView('reassign'));
                                        }}
                                    >
                                        Assign to this organisation
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
            {currentEditStep === 'activate-or-deactivate' && (
                <EditProjectUserActivatePanel
                    isActiveOrInvited={isActiveOrInvited}
                    activeStatusText={activeStatusText}
                    activeStatusIcon={activeStatusIcon}
                    isSubmitting={isSubmitting}
                    userSummary={editedUserDetailsCard}
                    statusIndicator={userStatusIndicator}
                    notifyCheckbox={notifyUserByEmailCheckbox}
                    backButton={backButton}
                    onConfirmActivationChange={submitUserActivationChange}
                />
            )}
            {currentEditStep === 'edit-user-details' && (
                <EditProjectUserProfileFieldsStep
                    formState={formState}
                    updateEditUserForm={updateEditUserForm}
                    isCountryCodeValid={isCountryCodeValid}
                    onPhoneCountryBlur={handlePhoneCountryBlur}
                    notifyCheckbox={notifyUserByEmailCheckbox}
                    backButton={backButton}
                    profileSaveButton={profileEditorSaveButton}
                    setErrorMessage={setErrorMessage}
                />
            )}
            {currentEditStep === 'unassign' && <UnassignUserFromOrganisation />}
            {currentEditStep === 'reassign' && <ReassignUserToOrganisation />}
            <div>{errorMessage && <ErrorMessage message={errorMessage} />}</div>
        </div>
    );
};
